import React, { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';

import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import useRecordMerchantSync from '@hooks/operator/useRecordMerchantSync';
import PageHeader from '@shared/PageHeader';
import StockTransferOrderForm from './components/StockTransferOrderForm';

interface InventoryTypeOption {
  id: string;
  code?: string;
  name: string;
  [key: string]: unknown;
}

interface MerchantInventoryType {
  merchant_id: string;
  enabled?: boolean;
  inventory_type: InventoryTypeOption;
}

interface STOLoaderData {
  id?: string;
  name?: string;
  merchant?: { id?: string } | null;
  merchant_id?: string;
  [key: string]: unknown;
}

const StockTransferOrderEdit: React.FC = () => {
  const { t } = useTranslation();
  const sto = useLoaderData() as STOLoaderData | null;

  // useRecordMerchantSync 在拿到 record 的 merchant_id 後會把 OperatorDataContext.currentMerchant
  // 對齊到該 merchant；merchantDataLoading 期間 form 不會 render，避免拿錯 merchant 的下拉資料。
  const { handleRecordLoaded, merchantDataLoading } = useRecordMerchantSync();
  const { merchantInventoryTypes, currentMerchant } = useOperatorData();

  // Wrapper 一掛載就把 loader 拿到的 record 餵給 sync hook；之後 useEffect 會把
  // OperatorDataContext.currentMerchant 切到該 record 所屬的 merchant。
  React.useEffect(() => {
    if (sto) handleRecordLoaded(sto);
  }, [sto, handleRecordLoaded]);

  const effectiveMerchantId = currentMerchant?.id ?? null;

  // Reference data 跟著 currentMerchant 走，sync 完成後才是 record 的 merchant。
  const inventoryTypes = useMemo<InventoryTypeOption[]>(() => {
    if (!effectiveMerchantId) return [];
    return (merchantInventoryTypes as MerchantInventoryType[] | undefined ?? [])
      .filter(mit => mit.merchant_id === effectiveMerchantId && mit.enabled !== false)
      .map(mit => mit.inventory_type);
  }, [merchantInventoryTypes, effectiveMerchantId]);

  return (
    <div>
      <PageHeader items={['stock_transfer_orders', 'stock_transfer_orders.list', { label: sto?.name }, { label: t('common.button.edit') }]} />
      <StockTransferOrderForm
        merchantId={sto?.merchant?.id}
        initialData={sto ?? undefined}
        inventoryTypes={inventoryTypes}
        merchantDataLoading={merchantDataLoading}
      />
    </div>
  );
};

export default StockTransferOrderEdit;
